import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Send, Trash2, Pencil, ExternalLink, ChevronDown, ChevronUp, FileText, Upload, Link as LinkIcon, Star, Ban } from "lucide-react";
import { toast } from "sonner";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";
import useBulkSelection from "@/hooks/useBulkSelection";

const STATUS_TONE = { invited: "blue", submitted: "yellow", changes_requested: "orange", active: "green", suspended: "red", inactive: "slate" };

const VENDOR_FIELDS = [
  { k: "company",         l: "Company *",        full: true },
  { k: "name",            l: "Name *" },
  { k: "primary_contact", l: "Primary contact" },
  { k: "email",           l: "Email *",          required: true },
  { k: "phone",           l: "Phone" },
  { k: "alt_mobile",      l: "Alt mobile" },
  { k: "website",         l: "Website" },
  { k: "category",        l: "Category", select: true },
  { k: "specialization",  l: "Specialization", full: true },
  { k: "address",         l: "Address", textarea: true, full: true },
  { k: "city",            l: "City" },
  { k: "state",           l: "State" },
  { k: "pin",             l: "PIN" },
  { k: "country",         l: "Country" },
  { k: "gstin",           l: "GSTIN" },
  { k: "pan",             l: "PAN" },
  { k: "payment_terms",   l: "Payment terms" },
  { k: "source",          l: "Source" },
  { k: "notes",           l: "Notes", textarea: true, full: true },
];

const blankForm = () => ({
  company: "", name: "", primary_contact: "", email: "", phone: "", alt_mobile: "",
  website: "", category: "", specialization: "", address: "", city: "", state: "",
  pin: "", country: "India", gstin: "", pan: "", payment_terms: "Net 30",
  source: "", notes: "", send_invite: true,
});

export default function AdminVendors() {
  const { user } = useAuth();
  const can = usePermissions();
  const canRate = can("vendors_admin");
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useSearchParams();
  const [categoryOptions, setCategoryOptions] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/vendors"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Phase 87 — Vendor category must match the Finance Taxonomy categories.
  useEffect(() => {
    api.get("/finance/categories")
      .then(({ data }) => setCategoryOptions((data?.categories || []).map((c) => c.category)))
      .catch(() => setCategoryOptions([]));
  }, []);

  const rate = async (vendorId, rating) => {
    try {
      await api.patch(`/admin/vendors/${vendorId}`, { rating });
      toast.success(rating < 2 ? "Rating saved — vendor auto-blacklisted" : "Rating saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // open dialog when ?new=1 in URL
  useEffect(() => {
    if (params.get("new") === "1") {
      setOpenNew(true);
      params.delete("new"); setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const save = async () => {
    if (!form.email.trim() || !form.name.trim() || !form.company.trim()) {
      toast.error("Company, Name and Email are required"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/vendors", form);
      toast.success(data.status === "invited"
        ? `Invite sent to ${data.email}.`
        : `Vendor ${data.name} created.`);
      setOpenNew(false); setForm(blankForm()); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const update = {};
      VENDOR_FIELDS.forEach((f) => { if (editing[f.k] !== undefined) update[f.k] = editing[f.k]; });
      delete update.email; // immutable
      await api.patch(`/admin/vendors/${editing.id}`, update);
      toast.success("Updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove vendor ${r.company}?`)) return;
    try { await api.delete(`/admin/vendors/${r.id}`); toast.success("Vendor removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="admin-vendors-page">
      <PageHeader
        eyebrow="Studio · Vendors"
        title="Vendors & suppliers"
        subtitle="Manufacturing partners, freelancers and any external company you pay. Invite them to share docs, sign contracts and submit invoices."
        actions={(
          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
            <BulkUploadDialog section="vendors" label="Vendors" onCommitted={load} />
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vendor-new"><Plus size={13} className="mr-1.5" />Onboard vendor</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">Onboard vendor</DialogTitle></DialogHeader>
              <p className="text-[11px] text-slate-500">They get a magic-link email and self-fill the rest. You&apos;ll approve from the queue before they can sign in.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {VENDOR_FIELDS.map((f) => (
                  <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
                    <Label className="label-overline">{f.l}</Label>
                    {f.select ? (
                      <Select value={form[f.k] || "none"} onValueChange={(v) => setForm({ ...form, [f.k]: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid={`vendor-form-${f.k}`}><SelectValue placeholder="— Select category —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : f.textarea ? (
                      <Textarea rows={2} className="rounded-sm mt-1 resize-none" value={form[f.k] || ""}
                        onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                        data-testid={`vendor-form-${f.k}`} />
                    ) : (
                      <Input className="rounded-sm mt-1" value={form[f.k] || ""}
                        onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                        data-testid={`vendor-form-${f.k}`} />
                    )}
                  </div>
                ))}
                <label className="sm:col-span-2 inline-flex items-center gap-2 text-[11px] text-slate-600">
                  <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} data-testid="vendor-form-send-invite" />
                  Send magic-link invite immediately
                </label>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vendor-save">
                  <Send size={13} className="mr-1.5" />{saving ? "Saving…" : (form.send_invite ? "Save & send invite" : "Save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      />

      {rows.length === 0 ? (
        <div className="card-flat p-12 text-center text-sm text-slate-500" data-testid="vendors-empty">
          No vendors yet. Click <b>Onboard vendor</b> to invite your first.
        </div>
      ) : (
        <VendorList rows={rows} setEditing={setEditing} remove={remove} canRate={canRate} onRate={rate} />
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Edit vendor · {editing?.company}</DialogTitle></DialogHeader>
          {editing && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {VENDOR_FIELDS.filter((f) => f.k !== "email").map((f) => (
                  <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
                    <Label className="label-overline">{f.l}</Label>
                    {f.select ? (
                      <Select value={editing[f.k] || "none"} onValueChange={(v) => setEditing({ ...editing, [f.k]: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="— Select category —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : f.textarea ? (
                      <Textarea rows={2} className="rounded-sm mt-1 resize-none" value={editing[f.k] || ""}
                        onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })} />
                    ) : (
                      <Input className="rounded-sm mt-1" value={editing[f.k] || ""}
                        onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })} />
                    )}
                  </div>
                ))}
              </div>
              <VendorDocsPanel vendorId={editing.id} />
            </>
          )}
          <DialogFooter>
            <Button onClick={saveEdit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vendor-edit-save">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Vendor documents panel — embedded inside the Edit Vendor dialog.
 * Accepts a PDF file OR a Google Drive / external URL. Synced to the
 * unified Documents Registry on add / edit / delete.
 * --------------------------------------------------------------------------*/
function VendorDocsPanel({ vendorId }) {
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("agreement");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    if (!vendorId) return;
    try { const { data } = await api.get(`/admin/vendors/${vendorId}/documents`); setDocs(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [vendorId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!link.trim()) { toast.error("Paste a Google Drive link"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("vendor_doc_type", docType);
      fd.append("drive_link", link.trim());
      await api.post(`/admin/vendors/${vendorId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added · synced to Registry");
      setTitle(""); setLink(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startEdit = (d) => { setEditingId(d.id); setDraft({ title: d.title, vendor_doc_type: d.vendor_doc_type, drive_link: d.drive_link || "" }); };
  const saveEdit = async () => {
    try {
      const payload = { title: draft.title, vendor_doc_type: draft.vendor_doc_type };
      if (draft.drive_link) payload.drive_link = draft.drive_link;
      await api.patch(`/vendor-documents/${editingId}`, payload);
      toast.success("Updated");
      setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? This removes it from the Registry too.`)) return;
    try { await api.delete(`/vendor-documents/${d.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [preview, setPreview] = useState(null); // { url, title }
  const openDoc = (d) => { if (d.drive_link) setPreview({ url: d.drive_link, title: d.title }); };

  return (
    <div className="mt-5 border-t border-slate-200 pt-4" data-testid="vendor-docs-panel">
      <div className="label-overline text-slate-500 mb-2">Documents · {docs.length}</div>

      <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <Input className="rounded-sm" placeholder="Title (e.g. MSA 2026)" value={title || ""} onChange={(e) => setTitle(e.target.value)} data-testid="vendor-doc-title" />
          <Input className="rounded-sm" placeholder="Type (agreement / profile / SOW…)" value={docType || ""} onChange={(e) => setDocType(e.target.value)} />
        </div>
        <Input className="rounded-sm" placeholder="https://drive.google.com/…" value={link || ""} onChange={(e) => setLink(e.target.value)} data-testid="vendor-doc-link" />
        <div className="flex justify-end mt-2">
          <Button onClick={submit} disabled={busy} size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="vendor-doc-add">
            <LinkIcon size={11} className="mr-1.5" />{busy ? "Saving…" : "Add document"}
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        {docs.length === 0 && <div className="text-[11px] text-slate-400 italic px-1">No documents yet — paste a Drive link above.</div>}
        {docs.map((d) => editingId === d.id ? (
          <div key={d.id} className="border border-orange-200 bg-orange-50/40 rounded-sm p-2 space-y-2" data-testid={`vendor-doc-edit-${d.id}`}>
            <Input className="rounded-sm h-8 text-xs" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            <Input className="rounded-sm h-8 text-xs" value={draft.vendor_doc_type} onChange={(e) => setDraft({ ...draft, vendor_doc_type: e.target.value })} />
            <Input className="rounded-sm h-8 text-xs" value={draft.drive_link} onChange={(e) => setDraft({ ...draft, drive_link: e.target.value })} />
            <div className="flex gap-1 justify-end">
              <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
              <Button size="sm" className="rounded-sm h-7 text-xs bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={saveEdit} data-testid={`vendor-doc-save-${d.id}`}>Save</Button>
            </div>
          </div>
        ) : (
          <div key={d.id} className="flex items-center gap-2 px-2 py-1.5 border border-slate-200 rounded-sm bg-white" data-testid={`vendor-doc-row-${d.id}`}>
            <LinkIcon size={12} className="text-blue-500 flex-shrink-0" />
            <button onClick={() => openDoc(d)} className="text-xs text-slate-700 hover:underline truncate flex-1 text-left">{d.title}</button>
            <span className="text-[10px] uppercase text-slate-400 tracking-wider">{d.vendor_doc_type}</span>
            <button onClick={() => startEdit(d)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`vendor-doc-edit-btn-${d.id}`}><Pencil size={11} /></button>
            <button onClick={() => remove(d)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`vendor-doc-delete-${d.id}`}><Trash2 size={11} /></button>
          </div>
        ))}
      </div>
      <DriveLinkPreviewModal open={!!preview} url={preview?.url} title={preview?.title} onClose={() => setPreview(null)} />
    </div>
  );
}


/* ----------------------------------------------------------------------------
 * Active vendors render as cards; everyone else collapses into a single list
 * (10 most-recent visible by default, the rest behind an arrow toggle).
 * "Recency" = updated_at falling back to created_at.
 * --------------------------------------------------------------------------*/
function VendorList({ rows, setEditing, remove, canRate, onRate }) {
  const [showAll, setShowAll] = useState(false);

  const { active, historical } = useMemo(() => {
    const byRecency = (a, b) =>
      new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    const active = rows.filter((r) => (r.status || "active") === "active").sort(byRecency);
    const historical = rows.filter((r) => (r.status || "active") !== "active").sort(byRecency);
    return { active, historical };
  }, [rows]);

  const bulk = useBulkSelection({
    collection: "users_vendor",
    ids: (rows || []).map((r) => r.id),
    onDone: () => window.location.reload(),
  });

  const visibleHistorical = showAll ? historical : historical.slice(0, 10);
  const hidden = historical.length - visibleHistorical.length;

  return (
    <>
      {bulk.canBulk && (rows.length > 0) && (
        <div
          className="inline-flex items-center gap-1.5 text-xs text-slate-600 px-2 py-1 border border-slate-200 rounded-sm bg-white mb-3 cursor-pointer"
          data-testid="vendors-select-all-label"
          onClick={() => bulk.toggleAll()}
        >
          <bulk.HeaderCheckbox /> <span>Select all</span>
        </div>
      )}
      {/* Active — card grid */}
      {active.length === 0 ? (
        <div className="card-flat p-8 text-center text-xs text-slate-500" data-testid="vendors-active-empty">
          No active vendors right now. Older vendors are listed below.
        </div>
      ) : (
        <div>
          <div className="label-overline mb-2 text-slate-500" data-testid="vendors-active-heading">
            Active · {active.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vendors-active-grid">
            {active.map((r) => (
              <VendorCard key={r.id} r={r} setEditing={setEditing} remove={remove} bulk={bulk} canRate={canRate} onRate={onRate} />
            ))}
          </div>
        </div>
      )}

      {/* Historical — collapsible list */}
      {historical.length > 0 && (
        <div className="mt-8" data-testid="vendors-historical-section">
          <div className="label-overline mb-2 text-slate-500">
            Historical · {historical.length}
          </div>
          <div className="card-flat divide-y divide-slate-100">
            {visibleHistorical.map((r) => (
              <VendorRow key={r.id} r={r} setEditing={setEditing} remove={remove} bulk={bulk} canRate={canRate} onRate={onRate} />
            ))}
            {hidden > 0 && (
              <button
                onClick={() => setShowAll(true)}
                className="w-full px-4 py-3 text-xs text-[#F54800] hover:bg-orange-50 inline-flex items-center justify-center gap-1.5 font-medium"
                data-testid="vendors-show-more"
              >
                <ChevronDown size={13} /> Show {hidden} more
              </button>
            )}
            {showAll && historical.length > 10 && (
              <button
                onClick={() => setShowAll(false)}
                className="w-full px-4 py-3 text-xs text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 font-medium"
                data-testid="vendors-show-less"
              >
                <ChevronUp size={13} /> Show less
              </button>
            )}
          </div>
        </div>
      )}
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
    </>
  );
}

/* Phase 87 — 1–5 star manual rating. Clicking a star (Admin/Founder only)
 * sets `rating`; dropping below 2 auto-blacklists server-side. */
function StarRating({ rating, canRate, onRate }) {
  const [hover, setHover] = useState(0);
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="inline-flex items-center gap-0.5" data-testid="vendor-star-rating" onMouseLeave={() => setHover(0)}>
      {stars.map((n) => {
        const filled = n <= (hover || rating || 0);
        return (
          <button
            key={n}
            type="button"
            disabled={!canRate}
            onClick={(e) => { e.stopPropagation(); canRate && onRate?.(n); }}
            onMouseEnter={() => canRate && setHover(n)}
            className={canRate ? "cursor-pointer" : "cursor-default"}
            title={canRate ? `Rate ${n} star${n === 1 ? "" : "s"}` : rating ? `${rating} star rating` : "Not rated"}
            data-testid={`vendor-star-${n}`}
          >
            <Star size={12} className={filled ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
          </button>
        );
      })}
    </div>
  );
}

function VendorCard({ r, setEditing, remove, bulk, canRate, onRate }) {
  return (
    <div className={`card-flat p-4 relative ${bulk?.isSelected(r.id) ? "ring-2 ring-[#F54800]" : ""}`} data-testid={`vendor-${r.id}`}>
      {bulk?.canBulk && (
        <div className="absolute top-2 right-2">
          <bulk.RowCheckbox id={r.id} />
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mb-1.5 pr-6">
        <div className="min-w-0">
          <div className="font-display font-bold text-sm text-slate-900 truncate">{r.company || "(no company)"}</div>
          <div className="text-[11px] text-slate-500 truncate">{r.name} · {r.email}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {r.blacklisted ? (
            <Tag tone="red" data-testid={`vendor-blacklisted-${r.id}`}><Ban size={9} className="inline mr-0.5" />Blacklisted</Tag>
          ) : (
            <Tag tone={STATUS_TONE[r.status] || "slate"}>{(r.status || "active").replace("_", " ")}</Tag>
          )}
        </div>
      </div>
      <div className="mb-1.5"><StarRating rating={r.rating} canRate={canRate} onRate={(n) => onRate?.(r.id, n)} /></div>
      <div className="text-[11px] text-slate-600 space-y-0.5">
        {r.category && <div>{r.category}{r.specialization ? ` · ${r.specialization}` : ""}</div>}
        {r.phone && <div>{r.phone}{r.alt_mobile ? ` / ${r.alt_mobile}` : ""}</div>}
        {(r.city || r.state) && <div>{[r.city, r.state, r.pin].filter(Boolean).join(", ")}</div>}
        {r.payment_terms && <div>Terms: {r.payment_terms}</div>}
        {r.gstin && <div className="font-mono text-[10px]">GST: {r.gstin}</div>}
      </div>
      <div className="flex items-center gap-1 mt-3">
        {r.website && (
          <a href={r.website} target="_blank" rel="noreferrer noopener" className="text-[10px] text-[#F54800] hover:underline inline-flex items-center gap-1">
            <ExternalLink size={9} /> Site
          </a>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setEditing(r)} className="text-slate-400 hover:text-slate-700 p-1.5" title="Edit" data-testid={`vendor-edit-${r.id}`}><Pencil size={11} /></button>
          <button onClick={() => remove(r)} className="text-slate-400 hover:text-red-600 p-1.5" title="Remove" data-testid={`vendor-remove-${r.id}`}><Trash2 size={11} /></button>
        </div>
      </div>
    </div>
  );
}

function VendorRow({ r, setEditing, remove, bulk, canRate, onRate }) {
  return (
    <div className={`px-4 py-3 grid grid-cols-12 gap-3 items-center text-xs hover:bg-slate-50 ${bulk?.isSelected(r.id) ? "bg-orange-50/40" : ""}`} data-testid={`vendor-row-${r.id}`}>
      {bulk?.canBulk && (
        <div className="col-span-1 flex items-center">
          <bulk.RowCheckbox id={r.id} />
        </div>
      )}
      <div className={bulk?.canBulk ? "col-span-2 min-w-0" : "col-span-3 min-w-0"}>
        <div className="font-medium text-slate-900 truncate">{r.company || "(no company)"}</div>
        <div className="text-[10px] text-slate-500 truncate">{r.name}</div>
      </div>
      <div className="col-span-2 text-slate-600 truncate">{r.email}</div>
      <div className="col-span-2 text-slate-600 truncate">{r.category || "—"}</div>
      <div className="col-span-2"><StarRating rating={r.rating} canRate={canRate} onRate={(n) => onRate?.(r.id, n)} /></div>
      <div className="col-span-2">
        {r.blacklisted ? (
          <Tag tone="red" data-testid={`vendor-row-blacklisted-${r.id}`}><Ban size={9} className="inline mr-0.5" />Blacklisted</Tag>
        ) : (
          <Tag tone={STATUS_TONE[r.status] || "slate"}>{(r.status || "—").replace("_", " ")}</Tag>
        )}
      </div>
      <div className="col-span-1 flex items-center justify-end gap-0.5">
        <button onClick={() => setEditing(r)} className="text-slate-400 hover:text-slate-700 p-1" title="Edit" data-testid={`vendor-row-edit-${r.id}`}><Pencil size={10} /></button>
        <button onClick={() => remove(r)} className="text-slate-400 hover:text-red-600 p-1" title="Remove" data-testid={`vendor-row-remove-${r.id}`}><Trash2 size={10} /></button>
      </div>
    </div>
  );
}
